// src/pages/ClientAdvisorSpace.jsx
// §8bis « Ce que voit le client relié » : espace de collaboration SIMPLIFIÉ (lecture).
// Ne reproduit PAS le dashboard Advisor. Montre le/les conseiller(s) qui gèrent un
// dossier pour le client, et UNIQUEMENT le contenu client-partagé (missions
// client_visible) — jamais les notes internes/marges (§8ter). Données via la RPC
// SECURITY DEFINER get_client_advisor_space. i18n FR/EN/DE.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { missionTypeLabel, missionStatusLabel } from "../config/advisorMissions";
import { PERMISSION_CATEGORIES, mergeGrants, permissionLabel } from "../config/dossierPermissions";
import { UserCircle, Package, Image, Briefcase, ArrowLeft, ShieldCheck, Plus, Paperclip, Send, FileText, ExternalLink } from "lucide-react";

const COPY = {
  fr: {
    title: "Mon conseiller", back: "Retour",
    subtitle: "Retrouvez ici le suivi partagé par votre Art Advisor. Vous gardez le contrôle : vous pouvez à tout moment gérer ou révoquer les accès depuis votre compte.",
    managedBy: "Suivi par", collections: "collections", artworks: "œuvres",
    missions: "Suivi partagé", noMissions: "Aucun suivi partagé pour le moment.",
    empty: "Aucun conseiller n’est relié à votre compte pour l’instant.",
    loading: "Chargement…",
    permissions: "Accès que vous accordez", permsHint: "Vous décidez de ce que votre conseiller peut consulter. Décochez pour révoker à tout moment.",
    contribTitle: "Compléter mon dossier", contribHint: "Ajoutez une information ou déposez un document pour votre conseiller.",
    add: "Ajouter", cancel: "Annuler", titleField: "Titre", notePh: "Information à transmettre…", file: "Document (facultatif)",
    submit: "Envoyer", submitting: "Envoi…", myContribs: "Mes contributions", noContribs: "Aucune contribution pour l’instant.",
    open: "Ouvrir", cStatus: { submitted: "En attente", validated: "Validé", rejected: "Refusé" },
    toFulfill: "Demandes de votre conseiller", respond: "Répondre",
  },
  en: {
    title: "My advisor", back: "Back",
    subtitle: "Here is the follow-up shared by your Art Advisor. You stay in control: you can manage or revoke access from your account at any time.",
    managedBy: "Managed by", collections: "collections", artworks: "artworks",
    missions: "Shared follow-up", noMissions: "Nothing shared yet.",
    empty: "No advisor is linked to your account yet.",
    loading: "Loading…",
    permissions: "Access you grant", permsHint: "You decide what your advisor can view. Uncheck to revoke at any time.",
    contribTitle: "Complete my file", contribHint: "Add information or upload a document for your advisor.",
    add: "Add", cancel: "Cancel", titleField: "Title", notePh: "Information to share…", file: "Document (optional)",
    submit: "Send", submitting: "Sending…", myContribs: "My contributions", noContribs: "No contribution yet.",
    open: "Open", cStatus: { submitted: "Pending", validated: "Validated", rejected: "Rejected" },
    toFulfill: "Requests from your advisor", respond: "Respond",
  },
  de: {
    title: "Mein Advisor", back: "Zurück",
    subtitle: "Hier finden Sie die von Ihrem Art Advisor geteilte Nachverfolgung. Sie behalten die Kontrolle: Zugriffe können Sie jederzeit in Ihrem Konto verwalten oder widerrufen.",
    managedBy: "Betreut von", collections: "Sammlungen", artworks: "Werke",
    missions: "Geteilte Nachverfolgung", noMissions: "Noch nichts geteilt.",
    empty: "Es ist noch kein Advisor mit Ihrem Konto verknüpft.",
    loading: "Laden…",
    permissions: "Von Ihnen gewährte Zugriffe", permsHint: "Sie entscheiden, was Ihr Advisor einsehen darf. Zum Widerruf abwählen.",
    contribTitle: "Meine Akte ergänzen", contribHint: "Fügen Sie eine Information hinzu oder laden Sie ein Dokument für Ihren Advisor hoch.",
    add: "Hinzufügen", cancel: "Abbrechen", titleField: "Titel", notePh: "Zu übermittelnde Information…", file: "Dokument (optional)",
    submit: "Senden", submitting: "Senden…", myContribs: "Meine Beiträge", noContribs: "Noch keine Beiträge.",
    open: "Öffnen", cStatus: { submitted: "Ausstehend", validated: "Bestätigt", rejected: "Abgelehnt" },
    toFulfill: "Anfragen Ihres Advisors", respond: "Antworten",
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

const STATUS_TONE = {
  in_progress: { bg: "#fbf6ea", c: "#8a6a2f" }, waiting_client: { bg: "#fbf6ea", c: "#8a6a2f" },
  waiting_third_party: { bg: "#fbf6ea", c: "#8a6a2f" }, ready_to_deliver: { bg: "#eef1f6", c: "#3a4a6b" },
  delivered: { bg: "#f1f6f1", c: "#2c5f2d" }, closed: { bg: "#f1f6f1", c: "#2c5f2d" },
  accepted: { bg: "#eef1f6", c: "#3a4a6b" }, proposed: { bg: "#eef1f6", c: "#3a4a6b" },
  draft: { bg: "#f5f5f4", c: "#57534e" }, cancelled: { bg: "#f5f5f4", c: "#57534e" },
};

export default function ClientAdvisorSpace() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);
  const locale = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2) === "de" ? "de-DE" : String(i18n.resolvedLanguage || "fr").slice(0, 2) === "en" ? "en-GB" : "fr-FR";

  const [loading, setLoading] = useState(true);
  const [dossiers, setDossiers] = useState([]);
  const [contribs, setContribs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [contribOpen, setContribOpen] = useState(null);   // entity_id | null
  const [cForm, setCForm] = useState({ title: "", note: "", file: null, request_id: null });
  const [cBusy, setCBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    const [{ data, error }, { data: cData }, { data: rData }] = await Promise.all([
      supabase.rpc("get_client_advisor_space"),
      supabase.from("advisor_client_contributions").select("*").eq("submitted_by", user.id).order("created_at", { ascending: false }),
      supabase.from("advisor_client_requests").select("*").eq("status", "requested").order("created_at", { ascending: false }),
    ]);
    if (!error && data?.ok) setDossiers(Array.isArray(data.dossiers) ? data.dossiers : []);
    else setDossiers([]);
    setContribs(Array.isArray(cData) ? cData : []);
    setReqs(Array.isArray(rData) ? rData : []);
    setLoading(false);
  }, [user?.id]);

  const submitContrib = useCallback(async (entityId, advisorMissing) => {
    if (!user?.id) return;
    const title = cForm.title.trim();
    const note = cForm.note.trim();
    if (!title && !note && !cForm.file) return;
    setCBusy(true);
    try {
      // advisor_id du dossier requis par la RLS d'insertion
      const { data: ent } = await supabase.from("advisor_client_entities").select("primary_advisor_id").eq("id", entityId).maybeSingle();
      const advisorId = ent?.primary_advisor_id;
      if (!advisorId) throw new Error("dossier");
      let storage_path = null, file_name = null, kind = "info";
      if (cForm.file) {
        const safe = cForm.file.name.replace(/[^\w.-]+/g, "_");
        const path = `${entityId}/${(window.crypto?.randomUUID?.() || Date.now())}-${safe}`;
        const { error: upErr } = await supabase.storage.from("client-contributions").upload(path, cForm.file, { upsert: false });
        if (upErr) throw upErr;
        storage_path = path; file_name = cForm.file.name; kind = "document";
      }
      const { error } = await supabase.from("advisor_client_contributions").insert({
        client_entity_id: entityId, advisor_id: advisorId, submitted_by: user.id,
        kind, title: title || file_name || null, note: note || null, storage_path, file_name,
        request_id: cForm.request_id || null,
      });
      if (error) throw error;
      setCForm({ title: "", note: "", file: null, request_id: null });
      setContribOpen(null);
      await load();
    } catch (e) {
      console.warn("[ClientAdvisorSpace] submitContrib", e);
      alert(e?.message || "");
    } finally {
      setCBusy(false);
    }
  }, [user?.id, cForm, load]);

  const openContribFile = useCallback(async (path) => {
    if (!path) return;
    const { data } = await supabase.storage.from("client-contributions").createSignedUrl(path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : null);

  const togglePerm = useCallback(async (entityId, key) => {
    let nextGrants = null;
    setDossiers((prev) => prev.map((d) => {
      if (d.entity_id !== entityId) return d;
      const cur = mergeGrants(d.granted_permissions);
      nextGrants = { ...cur, [key]: !cur[key] };
      return { ...d, granted_permissions: nextGrants };
    }));
    if (!nextGrants) return;
    const { data, error } = await supabase.rpc("set_dossier_permissions", { p_entity_id: entityId, p_permissions: nextGrants });
    if (error || data?.error) load(); // resync si échec
  }, [load]);

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f4", padding: "32px 20px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button onClick={() => navigate("/dashboard")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#6b6b6b", fontSize: 13, cursor: "pointer", marginBottom: 16 }}>
          <ArrowLeft size={16} /> {tx.back}
        </button>

        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>{tx.title}</h1>
        <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "8px 0 24px", maxWidth: 640 }}>{tx.subtitle}</p>

        {loading ? (
          <div style={{ color: "#9ca3af", fontSize: 14 }}>{tx.loading}</div>
        ) : dossiers.length === 0 ? (
          <div style={{ padding: 20, background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, color: "#6b6b6b", fontSize: 14 }}>{tx.empty}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {dossiers.map((d) => (
              <div key={d.entity_id} style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 24, boxShadow: "0 1px 3px rgba(26,26,26,0.04)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 4 }}>{tx.managedBy}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 34, height: 34, borderRadius: "50%", background: "#faf6ea", color: "#8b7355", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><UserCircle size={20} /></span>
                      <div>
                        <div style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a" }}>{d.advisor_name || "Art Advisor"}</div>
                        <div style={{ fontSize: 12.5, color: "#9ca3af" }}>{d.display_name}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#6b6b6b" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Package size={15} /> {d.collections_count || 0} {tx.collections}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Image size={15} /> {d.artworks_count || 0} {tx.artworks}</span>
                  </div>
                </div>

                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <Briefcase size={13} /> {tx.missions}
                  </div>
                  {(!d.missions || d.missions.length === 0) ? (
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>{tx.noMissions}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {d.missions.map((m) => {
                        const tone = STATUS_TONE[m.status] || STATUS_TONE.draft;
                        const due = fmtDate(m.due_at);
                        return (
                          <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", background: "#fafaf8", border: "1px solid #f2efe9", borderRadius: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 14, color: "#1a1a1a", fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.title}</div>
                              <div style={{ fontSize: 12, color: "#9ca3af" }}>{missionTypeLabel(m.mission_type, i18n.resolvedLanguage || i18n.language)}{due ? ` · ${due}` : ""}</div>
                            </div>
                            <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: tone.bg, color: tone.c }}>{missionStatusLabel(m.status, i18n.resolvedLanguage || i18n.language)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Permissions : le client contrôle et peut révoquer (§8ter) */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    <ShieldCheck size={13} /> {tx.permissions}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 10, lineHeight: 1.5 }}>{tx.permsHint}</div>
                  {(() => { const g = mergeGrants(d.granted_permissions); return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 6 }}>
                      {PERMISSION_CATEGORIES.map((cat) => (
                        <label key={cat.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 8, cursor: "pointer", background: g[cat.key] ? "#faf6ea" : "#fff" }}>
                          <input type="checkbox" checked={!!g[cat.key]} onChange={() => togglePerm(d.entity_id, cat.key)} style={{ width: 15, height: 15, accentColor: "#8b7355", flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: "#1a1a1a" }}>{permissionLabel(cat.key, i18n.resolvedLanguage || i18n.language)}</span>
                        </label>
                      ))}
                    </div>
                  ); })()}
                </div>

                {/* Demandes de l'Advisor à fournir (§8bis) */}
                {(() => { const open = reqs.filter((r) => r.client_entity_id === d.entity_id); return open.length === 0 ? null : (
                  <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8a6a2f", fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <Paperclip size={13} /> {tx.toFulfill} · {open.length}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {open.map((r) => (
                        <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: "#fbf6ea", border: "1px solid #ecdcae", borderRadius: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title}</div>
                            {r.description && <div style={{ fontSize: 12.5, color: "#8a6a2f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.description}</div>}
                          </div>
                          <button type="button" onClick={() => { setContribOpen(d.entity_id); setCForm({ title: r.title, note: "", file: null, request_id: r.id }); }}
                            style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                            <Send size={14} /> {tx.respond}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ); })()}

                {/* Contributions : le client complète son dossier (§8bis) */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #f2efe9" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                        <Paperclip size={13} /> {tx.contribTitle}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#9ca3af", marginTop: 2 }}>{tx.contribHint}</div>
                    </div>
                    <button type="button" onClick={() => { setContribOpen(contribOpen === d.entity_id ? null : d.entity_id); setCForm({ title: "", note: "", file: null, request_id: null }); }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", background: contribOpen === d.entity_id ? "#fff" : "#1a1a1a", color: contribOpen === d.entity_id ? "#1a1a1a" : "#fff", border: contribOpen === d.entity_id ? "1px solid #e5e3df" : "none", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
                      <Plus size={14} /> {contribOpen === d.entity_id ? tx.cancel : tx.add}
                    </button>
                  </div>

                  {contribOpen === d.entity_id && (
                    <div style={{ padding: 14, background: "#faf8f4", border: "1px solid #e5e3df", borderRadius: 8, marginBottom: 12, display: "grid", gap: 10 }}>
                      <input value={cForm.title} onChange={(e) => setCForm((p) => ({ ...p, title: e.target.value }))} placeholder={tx.titleField}
                        style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none" }} />
                      <textarea value={cForm.note} onChange={(e) => setCForm((p) => ({ ...p, note: e.target.value }))} placeholder={tx.notePh} rows={2}
                        style={{ padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, outline: "none", resize: "vertical" }} />
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#6b6b6b", cursor: "pointer" }}>
                        <Paperclip size={15} /> {cForm.file ? cForm.file.name : tx.file}
                        <input type="file" onChange={(e) => setCForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} style={{ display: "none" }} />
                      </label>
                      <div>
                        <button type="button" disabled={cBusy} onClick={() => submitContrib(d.entity_id)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: cBusy ? "default" : "pointer", opacity: cBusy ? 0.6 : 1 }}>
                          <Send size={15} /> {cBusy ? tx.submitting : tx.submit}
                        </button>
                      </div>
                    </div>
                  )}

                  {(() => { const mine = contribs.filter((c) => c.client_entity_id === d.entity_id); return mine.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: "#9ca3af" }}>{tx.noContribs}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {mine.map((c) => {
                        const tone = c.status === "validated" ? { bg: "#f1f6f1", c: "#2c5f2d" } : c.status === "rejected" ? { bg: "#f8eef0", c: "#8f3a44" } : { bg: "#fbf6ea", c: "#8a6a2f" };
                        return (
                          <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 11px", background: "#fff", border: "1px solid #e5e3df", borderRadius: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13.5, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {c.kind === "document" && <FileText size={13} style={{ flexShrink: 0, color: "#8b7355" }} />}{c.title || c.file_name || "—"}
                              </div>
                              {c.note && <div style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.note}</div>}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                              {c.storage_path && (
                                <button type="button" onClick={() => openContribFile(c.storage_path)} title={tx.open}
                                  style={{ background: "transparent", border: "none", color: "#8b7355", cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                                  <ExternalLink size={14} />
                                </button>
                              )}
                              <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: tone.bg, color: tone.c }}>{tx.cStatus[c.status] || c.status}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ); })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
